// Frontend types matching the Drizzle schema in src/server/schema.ts
// Timestamps are ISO strings; nullable DB columns use `| null`.

export interface FeatureToggles {
  pollsEnabled: boolean;
  shopEnabled: boolean;
  directoryEnabled: boolean;
  bulletinEnabled: boolean;
  calendarEnabled: boolean;
  cateringEnabled: boolean;
  mapEnabled: boolean;
  paymentsEnabled: boolean;
  villasEnabled: boolean;
  publicRegistrationEnabled: boolean;
  reunion2027RevealEnabled: boolean;
}

export const DEFAULT_FEATURE_TOGGLES: FeatureToggles = {
  pollsEnabled: true,
  shopEnabled: false,
  directoryEnabled: true,
  bulletinEnabled: true,
  calendarEnabled: true,
  cateringEnabled: false,
  mapEnabled: true,
  paymentsEnabled: false,
  villasEnabled: false,
  publicRegistrationEnabled: false,
  // Reveal is public — default ON so the reveal renders on first paint (no
  // flag-check flicker). The DB toggle is also ON; this removes the brief
  // flash of the gated/teaser version during initial load.
  reunion2027RevealEnabled: true,
};

export interface Reunion {
  id: string;
  name: string;
  slug: string;
  familyGroup: string;
  year: number;
  startDate: string | null;
  endDate: string | null;
  location: string | null;
  description: string | null;
  status: 'planning' | 'active' | 'completed';
  registrationOpen: boolean;
  registrationOpenDate: string | null;
  registrationCloseDate: string | null;
  depositAmount: number; // cents
  depositPerPerson: number; // cents — per-person deposit tier
  depositDueDate: string | null;
  costPerAdult: number; // cents
  costPerChild: number; // cents
  paymentDeadline: string | null;
  themeColor: string;
  logoUrl: string | null;
  tagline: string | null;
  heroImageUrl: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  featureToggles: FeatureToggles;
  createdAt: string;
  updatedAt: string;
}

// Lightweight type for list endpoints (omits large text fields)
export type ReunionSummary = Pick<Reunion,
  'id' | 'name' | 'slug' | 'familyGroup' | 'year' | 'status' |
  'registrationOpen' | 'themeColor' | 'logoUrl' | 'tagline' | 'startDate' | 'endDate'
>;

// Full type alias for clarity
export type ReunionDetail = Reunion;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  profileImageUrl: string | null;
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserSession {
  id: string;
  userId: string;
  reunionId: string | null;
  deviceInfo: string | null;
  ipAddress: string | null;
  expiresAt: string;
  lastActivityAt: string;
  revokedAt: string | null;
  createdAt: string;
}

export interface Role {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
  createdAt: string;
}

export interface UserRoleAssignment {
  id: string;
  userId: string;
  roleId: string;
  reunionId: string | null;
  assignedBy: string | null;
  assignedAt: string;
}

export type UserRole = 'super_admin' | 'reunion_admin' | 'group_leader' | 'member';

// ── Auth request/response types ──────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
  reunionId?: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  familyGroup: string;
  inviteToken?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  profileImageUrl: string | null;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AuthUserRole {
  roleSlug: string;
  roleName: string;
  reunionId: string | null;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
  roles: AuthUserRole[];
}

export interface RegisterResponse {
  user: Pick<AuthUser, 'id' | 'email' | 'firstName' | 'lastName' | 'emailVerified' | 'createdAt'>;
  message: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  newPassword: string;
}

export interface MessageResponse {
  message: string;
}

// ── RSVP types ──────────────────────────────────────────────────────

export type RsvpStatus = 'attending' | 'not_attending' | 'pending' | 'waitlisted';

export interface Rsvp {
  id: string;
  eventId: string;
  userId: string;
  reunionId: string;
  status: RsvpStatus;
  respondedAt: string | null;
  createdAt: string;
}

export interface RsvpWithUser extends Rsvp {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface EventRsvpSummary {
  eventId: string;
  attendingCount: number;
  notAttendingCount: number;
  pendingCount: number;
  waitlistedCount: number;
  capacityLimit: number | null;
  userRsvpStatus: RsvpStatus | null;
}

// ── Day Plan Template types ─────────────────────────────────────────

export interface DayPlanTemplate {
  id: string;
  name: string;
  reunionId: string;
  description: string | null;
  eventBlueprints: string; // JSON
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}
